import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRightLeft, Check, ChevronDown, CircleSlash, MoreVertical, Pencil, RotateCcw, Send, Trash2, Undo2, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { costChangeStatusVariant } from '@/lib/status-variants'
import { changeStatusView, type ChangeAffordances } from '../lib/changes'
import { CHANGE_KIND_KEY } from '../lib/labels'
import type { CostChange } from '../types'

interface Props {
  change: CostChange
  af: ChangeAffordances
  /** i18n key: why submitting would be refused, or null. */
  blocker: string | null
  onBack: () => void
  onEdit: () => void
  onSubmit: () => void
  onApprove: () => void
  onReject: () => void
  onReopen: () => void
  onUnapprove: () => void
  onDelete: () => void
  onConvertBudget: () => void
  onConvertContract: () => void
  onCreateRfq: () => void
  onDismiss: () => void
}

const ChangeHeader: React.FC<Props> = ({
  change, af, blocker, onBack, onEdit, onSubmit, onApprove, onReject, onReopen, onUnapprove, onDelete, onConvertBudget, onConvertContract, onCreateRfq, onDismiss,
}) => {
  const { t } = useTranslation()
  const status = changeStatusView(change)
  return (
    <div className="flex flex-wrap items-start gap-3">
      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onBack} aria-label={t('CostControl_change_back')}>
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm text-muted-foreground">{t('CostControl_change_number', { number: change.number })}</span>
          <h1 className="truncate text-xl font-semibold">{change.title}</h1>
          <Badge variant={costChangeStatusVariant(status.tone)}>{t(status.key)}</Badge>
          <Badge variant={change.kind === 'POTENTIAL' ? 'neutral' : 'secondary'}>{t(CHANGE_KIND_KEY[change.kind])}</Badge>
          {change.takenOver && <Badge variant="secondary">{t('CostControl_change_taken_over')}</Badge>}
          {change.drawsProvisional && <Badge variant="secondary">{t('CostControl_provisional_draws_badge')}</Badge>}
        </div>
        {change.contractName && <p className="text-sm text-muted-foreground">{change.contractName}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {af.submit && (
          <Button size="sm" disabled={!!blocker} onClick={onSubmit}>
            <Send className="h-4 w-4" />
            {t('CostControl_change_submit')}
          </Button>
        )}
        {(af.convert || af.createRfq) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm">
                <ArrowRightLeft className="h-4 w-4" />
                {t('CostControl_potential_convert')}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {af.convert && <DropdownMenuItem onSelect={onConvertBudget}>{t('CostControl_potential_convert_budget')}</DropdownMenuItem>}
              {af.convert && <DropdownMenuItem onSelect={onConvertContract}>{t('CostControl_potential_convert_contract')}</DropdownMenuItem>}
              {af.createRfq && <DropdownMenuItem onSelect={onCreateRfq}>{t('CostControl_potential_create_rfq')}</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {af.dismiss && (
          <Button variant="outline" size="sm" onClick={onDismiss}>
            <CircleSlash className="h-4 w-4" />
            {t('CostControl_potential_dismiss')}
          </Button>
        )}
        {af.approve && (
          <Button size="sm" onClick={onApprove}>
            <Check className="h-4 w-4" />
            {t('CostControl_change_approve')}
          </Button>
        )}
        {af.reject && (
          <Button variant="outline" size="sm" onClick={onReject}>
            <X className="h-4 w-4" />
            {t('CostControl_change_reject')}
          </Button>
        )}
        {af.reopen && (
          <Button variant="outline" size="sm" onClick={onReopen}>
            <RotateCcw className="h-4 w-4" />
            {t('CostControl_change_reopen')}
          </Button>
        )}
        {af.unapprove && (
          <Button variant="outline" size="sm" onClick={onUnapprove}>
            <Undo2 className="h-4 w-4" />
            {t('CostControl_change_unapprove')}
          </Button>
        )}
        {af.editHeader && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
            {t('Common_edit')}
          </Button>
        )}
        {af.remove && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('CostControl_change_more')}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={onDelete}>
                <Trash2 className="h-4 w-4" />
                {t('Common_delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {af.submit && blocker && <p className="w-full pl-11 text-xs text-muted-foreground">{t(blocker)}</p>}
    </div>
  )
}
export default ChangeHeader
